// Redactar un correo nuevo desde un buzon del CRM: el personal o el de uno de tus grupos.
// La firma de Mi perfil se agrega sola al final.
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';

import { type Adjunto, enviar, MAX_ADJUNTOS_MB, subirAdjunto } from '../api/correo';
import { useSession } from '../auth/session';
import { borrarBorrador, guardarBorrador, nuevoIdBorrador, useBorradores } from '../lib/borradores';
import { asuntoConTicket, conCopiaAlGrupo } from '../lib/casoDeHilo';
import { destinatarios } from '../lib/correo';
import { Icon } from '../lib/icons';
import { useBuzonesVisibles } from '../screens/bandeja/useHilos';
import { AvisoCasoEnCorreo, useCasoEnCorreo } from './AvisoCasoEnCorreo';
import { BuzonDialog } from './Buzones';
import { Dialog } from './Dialog';
import { InsertarArticulo } from './InsertarArticulo';
import { TextoRapido, useFirma } from './Plantillas';
import { toast } from './toasts';

export function Redactor({ para = '', cc = '', asunto = '', texto = '', desde, borrador, onClose }: { para?: string; cc?: string; asunto?: string; texto?: string; desde?: string; borrador?: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { yo } = useSession();
  // Solo se envia desde tus buzones y los de tus grupos (el administrador, todos).
  const r = useBuzonesVisibles();
  const usables = (r.data ?? []).filter((b) => !b.fallo);
  const guardado = useBorradores().find((x) => x.id === borrador);
  const [v, setV] = useState(guardado ? { cuentaId: guardado.cuentaId, para: guardado.para, cc: guardado.cc, cco: guardado.cco, asunto: guardado.asunto, texto: guardado.texto } : { cuentaId: desde ?? '', para, cc, cco: '', asunto, texto });
  const [conCc, setConCc] = useState(!!(v.cc || v.cco));
  // Se guarda como borrador lo que cambie respecto a como se abrio (un "Responder" sin tocar no deja borrador).
  const id = useRef(borrador ?? nuevoIdBorrador());
  const inicial = useRef(JSON.stringify(v));
  useEffect(() => {
    if (JSON.stringify(v) === inicial.current) return;
    const t = setTimeout(() => guardarBorrador({ id: id.current, ...v }), 600);
    return () => clearTimeout(t);
  }, [v]);
  const ultimo = useRef(v);
  ultimo.current = v;
  useEffect(
    () => () => {
      if (JSON.stringify(ultimo.current) !== inicial.current) guardarBorrador({ id: id.current, ...ultimo.current });
    },
    [],
  );
  const descartar = () => {
    inicial.current = JSON.stringify(v);
    borrarBorrador(id.current);
    onClose();
  };
  const [enviando, setEnviando] = useState(false);
  const [adjuntos, setAdjuntos] = useState<Adjunto[]>([]);
  const [subiendo, setSubiendo] = useState(0);
  const adjuntar = async (archivos: FileList | null) => {
    if (!archivos?.length) return;
    const lista = [...archivos];
    const total = [...adjuntos, ...lista.map((a) => ({ tamano: a.size }))].reduce((n, a) => n + a.tamano, 0);
    if (total > MAX_ADJUNTOS_MB * 1024 * 1024) return setError(`Los adjuntos pasan de ${MAX_ADJUNTOS_MB} MB. Envía un enlace o divide el envío.`);
    setError('');
    setSubiendo((n) => n + lista.length);
    for (const a of lista) {
      try {
        const subido = await subirAdjunto(a);
        setAdjuntos((l) => [...l, subido]);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
  };
  const [error, setError] = useState('');
  const [conectar, setConectar] = useState(false);
  const cuentaId = v.cuentaId || usables[0]?.id || '';
  const firma = useFirma(usables.find((b) => b.id === cuentaId)?.correo);
  // Capa 1: un correo a un cliente con el asunto de uno de sus casos abiertos (p. ej. "Responder" desde otra pantalla)
  // se ofrece enviar desde el caso; si sale igual desde el buzon personal, va copia oculta al buzon del grupo.
  const participantes = [...destinatarios(v.para).validos, ...destinatarios(v.cc).validos];
  const enCorreo = useCasoEnCorreo({ asunto: v.asunto, participantes, activo: !!v.asunto.trim() && participantes.length > 0 });
  const elegido = usables.find((b) => b.id === cuentaId);
  const personal = enCorreo.esPersonal(elegido);
  const ccoGrupo = personal && enCorreo.coincide && enCorreo.grupo?.correo ? enCorreo.grupo.correo : null;

  const mandar = async (e: FormEvent) => {
    e.preventDefault();
    const a = destinatarios(v.para);
    const c = destinatarios(v.cc);
    const o = destinatarios(v.cco);
    if (a.invalidos.length || c.invalidos.length || o.invalidos.length) return setError(`Revisa estas direcciones: ${[...a.invalidos, ...c.invalidos, ...o.invalidos].join(', ')}`);
    if (!a.validos.length) return setError('Escribe al menos un destinatario.');
    setError('');
    setEnviando(true);
    try {
      const cco = ccoGrupo ? conCopiaAlGrupo({ para: a.validos, cc: c.validos, cco: o.validos }, ccoGrupo) : o.validos;
      await enviar({ cuentaId, para: a.validos, cc: c.validos, cco, asunto: v.asunto, texto: v.texto, firma, adjuntos });
      inicial.current = JSON.stringify(v);
      borrarBorrador(id.current);
      void qc.invalidateQueries({ queryKey: ['correos'] });
      void qc.invalidateQueries({ queryKey: ['timeline'] });
      toast('Correo enviado.');
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  if (conectar) return <BuzonDialog correoInicial={yo.email} onClose={() => setConectar(false)} />;
  if (!r.isLoading && !usables.length)
    return (
      <Dialog titulo="Nuevo correo" onClose={onClose}>
        <div className="form">
          <p style={{ margin: 0 }}>Para enviar correos desde el CRM, conecta primero tu correo. Solo toma un minuto: tu dirección y su contraseña.</p>
          <div className="actions">
            <button className="btn ghost" onClick={onClose}>
              Cancelar
            </button>
            <button className="btn primary" onClick={() => setConectar(true)}>
              <Icon name="mail" /> Conectar mi correo
            </button>
          </div>
        </div>
      </Dialog>
    );

  return (
    <Dialog titulo="Nuevo correo" onClose={onClose} width={640}>
      <form className="form" onSubmit={mandar}>
        <AvisoCasoEnCorreo
          info={enCorreo}
          className="redactor-aviso-caso"
          personal={personal}
          desdeCaso={!!enCorreo.cuentaGrupo && cuentaId === enCorreo.cuentaGrupo.id}
          reenvio={false}
          onDesdeCaso={() => enCorreo.cuentaGrupo && setV({ ...v, cuentaId: enCorreo.cuentaGrupo.id, asunto: asuntoConTicket(v.asunto, enCorreo.coincide?.caso.numero ?? null) })}
        />
        {usables.length > 1 ? (
          <label>
            Desde
            <select id="c-desde" value={cuentaId} onChange={(e) => setV({ ...v, cuentaId: e.target.value })}>
              {usables.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.correo}
                </option>
              ))}
            </select>
          </label>
        ) : usables[0] ? (
          <p className="note">Desde {usables[0].correo}</p>
        ) : null}
        <label>
          Para
          <input id="c-para" value={v.para} onChange={(e) => setV({ ...v, para: e.target.value })} placeholder="nombre@empresa.com, otro@empresa.com" required autoComplete="off" />
        </label>
        {conCc ? (
          <div className="grid2">
            <label>
              Copia (CC)
              <input id="c-cc" value={v.cc} onChange={(e) => setV({ ...v, cc: e.target.value })} autoComplete="off" />
            </label>
            <label>
              Copia oculta (CCO)
              <input id="c-cco" value={v.cco} onChange={(e) => setV({ ...v, cco: e.target.value })} autoComplete="off" />
            </label>
          </div>
        ) : (
          <button type="button" className="linkish" style={{ alignSelf: 'flex-start' }} onClick={() => setConCc(true)}>
            Agregar copia (CC / CCO)
          </button>
        )}
        <label>
          Asunto
          <input id="c-asunto" value={v.asunto} onChange={(e) => setV({ ...v, asunto: e.target.value })} required autoComplete="off" />
        </label>
        <label>
          Mensaje
          <textarea id="c-texto" rows={texto ? 14 : 9} value={v.texto} onChange={(e) => setV({ ...v, texto: e.target.value })} required />
        </label>
        {adjuntos.length || subiendo ? (
          <div className="adjuntos">
            {adjuntos.map((a) => (
              <span key={a.id} className="adjunto">
                <Icon name="paperclip" size={13} /> {a.name} <small>{tamanoTexto(a.tamano)}</small>
                <button type="button" aria-label={`Quitar ${a.name}`} onClick={() => setAdjuntos((l) => l.filter((x) => x.id !== a.id))}>
                  ×
                </button>
              </span>
            ))}
            {subiendo ? <span className="adjunto subiendo">Subiendo {subiendo}…</span> : null}
          </div>
        ) : null}
        <div className="redactor-pie">
          <label className="btn sm ghost">
            <Icon name="paperclip" size={14} /> Adjuntar
            <input id="c-adjuntos" type="file" multiple hidden onChange={(e) => (void adjuntar(e.target.files), (e.target.value = ''))} />
          </label>
          <TextoRapido datos={{}} onElegir={(t) => setV((x) => ({ ...x, texto: x.texto ? `${x.texto.replace(/\s+$/, '')}\n${t}` : t }))} />
          <InsertarArticulo onElegir={(t) => setV((x) => ({ ...x, texto: x.texto.trim() ? `${x.texto.replace(/\s+$/, '')}\n\n${t}` : t }))} />
          <span className="note">{firma ? `Firma: ${firma.split('\n')[0]}…` : 'Sin firma (se configura en Mi perfil)'}</span>
        </div>
        {error ? <p className="error">{error}</p> : null}
        <div className="actions">
          {borrador || JSON.stringify(v) !== inicial.current ? (
            <button type="button" className="btn ghost" onClick={descartar} style={{ marginRight: 'auto' }}>
              <Icon name="trash" /> Descartar
            </button>
          ) : null}
          <button type="button" className="btn ghost" onClick={onClose}>
            {JSON.stringify(v) !== inicial.current ? 'Guardar y cerrar' : 'Cancelar'}
          </button>
          <button className="btn primary" disabled={enviando || !cuentaId || subiendo > 0}>
            <Icon name="send" /> {enviando ? 'Enviando…' : 'Enviar'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

const tamanoTexto = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
